import { ProductAttributes } from '@app/components/products/ProductAttributes';
import { ProductPrice } from '@app/components/products/ProductPrice';
import { CheckoutProductSummary } from '@app/components/products/ProductSummary/CheckoutProductSummary';
import { CustomImage } from '@app/components/various/CustomImage';
import { Tooltip } from '@app/components/various/Tooltip';
import { createProductImageSelector, getDefaultImageAspectRatio } from '@app/ducks/helpers';
import { getOrderCurrency, getOrderStatus, getProductInfo } from '@app/ducks/order';
import { getIsOrderEditable } from '@app/logic/Orders';
import { formatPriceWithCurrencyAffix } from '@app/logic/price';
import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import { Product } from '@typings';
import { t } from 'i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { SummaryValues } from './CheckoutProduct';

interface Props {
  productInfo: Product.Standard;
  summaryValues: SummaryValues;
  isDisabled: boolean;
  link: string | undefined;
}

const getMedia = createProductImageSelector(getProductInfo);

export const CheckoutProductInfoMobile = (props: Props) => {
  const { isDisabled, productInfo, summaryValues, link } = props;
  const productId = productInfo.product;
  const currency = useSelector(getOrderCurrency);
  const aspectRatio = useSelector(getDefaultImageAspectRatio);
  const imageSrc = useSelector((state: Store) => getMedia(state, productId)).src;
  const orderStatus = useSelector(getOrderStatus);
  const isOrderEditable = getIsOrderEditable(orderStatus);

  return (
    <div className={cn('flex flex-col overflow-hidden', { 'pointer-events-none': isDisabled && isOrderEditable })}>
      <div className="relative flex flex-col gap-x-4 gap-y-6 xs:flex-row">
        <div className={cn('row-span-2 h-[106px] w-18 shrink-0', { 'opacity-50': isDisabled && isOrderEditable })}>
          {isDefined(link) ?
            <Link to={link} className="block h-full">
              <CustomImage
                size="cover"
                aspectRatio={aspectRatio}
                src={imageSrc}
                placeholderClassName="rounded-small overflow-hidden h-full"
                imageClassName="h-full w-full"
              />
            </Link>
          : <Tooltip
              overlayClassName="max-w-52"
              content={isDisabled && !isOrderEditable ? t('products:currently_not_available') : undefined}
          >
              <div className="h-full">
                <CustomImage
                  size="cover"
                  aspectRatio={aspectRatio}
                  src={imageSrc}
                  placeholderClassName="rounded-small overflow-hidden"
                  imageClassName="h-full w-full"
                />
              </div>
            </Tooltip>
          }
        </div>
        <div className="flex grow flex-col">
          <div className="mb-3 flex items-start justify-between gap-4">
            <Tooltip overlayClassName="max-w-52" content={productInfo.name} disableOnTouchScreen>
              {isDefined(link) ?
                <Link
                  tabIndex={isDefined(link) ? 0 : -1}
                  to={link}
                  className={cn('flex cursor-pointer flex-col pr-10', { 'opacity-50': isDisabled && isOrderEditable })}
                >
                  <span>
                    <h2 className="line-clamp-2 body-1">{productInfo.name}</h2>
                    <ProductPrice productInfo={productInfo} />
                  </span>
                </Link>
              : <span>
                  <h2 className="line-clamp-2 body-1">{productInfo.name}</h2>
                  <ProductPrice productInfo={productInfo} />
                </span>
              }
            </Tooltip>
          </div>
          <CheckoutProductSummary
            layout="vertical"
            variantsCount={summaryValues.variantsCount}
            unitsCount={summaryValues.unitsCount}
            price={formatPriceWithCurrencyAffix(currency)(summaryValues.price)}
            disabled={isDisabled && isOrderEditable}
          />
        </div>
      </div>
      <div className="pb-3">
        <ProductAttributes product={productInfo} isDisabled={isDisabled} isCompact isCheckout />
      </div>
    </div>
  );
};
